import { useEffect, useMemo, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { formatApiError } from "@/lib/format-api-error";
import { usePartners } from "../../partners/use-partners";
import { useAssignJourney } from "./use-assign-journey";

// Phase 7 sub-bucket 14 — bulk-assign Dialog. Mirrors
// /app/admin/journeys/_components/AssignJourneyDialog.tsx: search +
// scrollable list with checkboxes; already-assigned partners are disabled
// (greyed out) so admins don't double-assign. Submit calls the bulk
// POST /api/admin/journeys/:id/assignments endpoint (single network round
// trip — `created` may be < selected count if a partner slipped in via
// another tab between render and submit).

export function AssignPartnersDialog({
  journeyId,
  assignedPartnerIds,
}: {
  journeyId: string;
  assignedPartnerIds: Set<string>;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const partnersQuery = usePartners();
  const assignJourney = useAssignJourney();

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelected(new Set());
    }
  }, [open]);

  const filtered = useMemo(() => {
    const rows = partnersQuery.data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (p) =>
        p.company_name.toLowerCase().includes(term) ||
        (p.primary_contact_name ?? "").toLowerCase().includes(term),
    );
  }, [partnersQuery.data, search]);

  function toggle(partnerId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(partnerId)) next.delete(partnerId);
      else next.add(partnerId);
      return next;
    });
  }

  function handleSubmit() {
    const partnerIds = Array.from(selected);
    if (partnerIds.length === 0) return;
    assignJourney.mutate(
      { journeyId, partnerIds },
      {
        onSuccess: ({ created }) => {
          const dupes = partnerIds.length - created;
          if (dupes > 0) {
            toast.success(
              `${created} assigned (${dupes} were already assigned)`,
            );
          } else {
            toast.success(`${created} partner${created === 1 ? "" : "s"} assigned`);
          }
          setOpen(false);
        },
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to assign partners")),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <UserPlus className="mr-1.5 h-3.5 w-3.5" />
          Assign partners
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Assign partners to this journey
          </DialogTitle>
          <DialogDescription>
            Selecting a partner spawns one progress row per task. Partners
            already assigned to this journey are shown disabled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company or contact"
            autoFocus
          />

          <div className="max-h-72 overflow-y-auto rounded-md border bg-card">
            {partnersQuery.isLoading ? (
              <div className="space-y-2 p-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                {search.trim()
                  ? "No partners match your search."
                  : "No partners available."}
              </p>
            ) : (
              <ul className="divide-y divide-border/70">
                {filtered.map((p) => {
                  const alreadyAssigned = assignedPartnerIds.has(p.id);
                  const isSelected = selected.has(p.id);
                  return (
                    <li
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm"
                    >
                      <Checkbox
                        id={`partner-${p.id}`}
                        checked={isSelected}
                        disabled={alreadyAssigned}
                        onCheckedChange={() => toggle(p.id)}
                      />
                      <label
                        htmlFor={`partner-${p.id}`}
                        className={`flex flex-1 cursor-pointer items-center justify-between ${
                          alreadyAssigned ? "text-muted-foreground" : ""
                        }`}
                      >
                        <div>
                          <p className="font-medium">{p.company_name}</p>
                          {p.primary_contact_name && (
                            <p className="text-xs text-muted-foreground">
                              {p.primary_contact_name}
                            </p>
                          )}
                        </div>
                        {alreadyAssigned && (
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            Assigned
                          </span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            {selected.size} selected
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={assignJourney.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={assignJourney.isPending || selected.size === 0}
          >
            {assignJourney.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Assign {selected.size > 0 ? `${selected.size} ` : ""}partner
            {selected.size === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
